/*
  Southern Oaks buy box (Movy Media, 2026-09-28).
  Handles variant tiles, gallery, one-time vs subscription (Shopify selling plans),
  the optional add-on product, the purchase acknowledgment (bee venom), and add to bag into Dawn's cart drawer.
  2026-10-01 conversion features: express checkout visibility, the phone sticky Add to Bag bar,
  back in stock signup (Klaviyo client API), gift card recipient properties, and the holiday order-by note.
  2026-10-06: Strength request tracker (by-review products post sent requests to the tracker webhook).
*/
if (!customElements.get('so-buy-box')) {
  customElements.define(
    'so-buy-box',
    class SoBuyBox extends HTMLElement {
      connectedCallback() {
        this.setupReadMore();
        // By review (tag by-review): no bag form, the request form is a plain contact form. Gallery only.
        if (this.hasAttribute('data-review')) {
          this.querySelectorAll('.so-bb__thumb').forEach((btn) =>
            btn.addEventListener('click', () => this.showMedia(btn.dataset.mediaId))
          );
          this.setupRequestTracker();
          return;
        }
        const json = this.querySelector('[data-product-json]');
        if (!json) return;
        this.product = JSON.parse(json.textContent);
        const addonJson = this.querySelector('[data-addon-json]');
        this.addon = addonJson ? JSON.parse(addonJson.textContent) : null;
        this.currency = this.dataset.currency || 'USD';
        this.buttonLabel = this.dataset.buttonLabel || 'Add to Bag';
        this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';

        this.form = this.querySelector('form.so-bb__form');
        this.variantInput = this.querySelector('[data-variant-input]');
        this.planInput = this.querySelector('[data-plan-input]');
        this.atc = this.querySelector('[data-atc]');
        this.atcLabel = this.querySelector('[data-atc-label]');
        this.errorEl = this.querySelector('[data-error]');
        this.addonInput = this.querySelector('[data-addon-input]');
        // Purchase acknowledgment (bee venom): required before Add to Bag, saved as a line item property
        this.ackInput = this.querySelector('[data-ack-input]');
        this.ackMsg = this.querySelector('[data-ack-msg]');
        this.ackLabel = this.dataset.ackLabel || '';
        this.ackValue = this.dataset.ackValue || '';
        this.ackVersion = this.dataset.ackVersion || '';
        // Conversion features (each one is absent when switched off in the theme editor)
        this.express = this.querySelector('[data-express]');
        this.gift = this.querySelector('[data-gift]');
        this.holiday = this.querySelector('[data-holiday]');
        this.restock = this.querySelector('[data-restock]');
        this.setupBackInStock();
        this.checkHoliday();

        const current = this.product.variants.find((v) => String(v.id) === this.variantInput.value);
        this.variant = current || this.product.variants[0];
        this.planId = this.planInput.value || '';

        this.addEventListener('change', this.onChange.bind(this));
        this.querySelectorAll('.so-bb__thumb').forEach((btn) =>
          btn.addEventListener('click', () => this.showMedia(btn.dataset.mediaId))
        );
        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.setupSticky();
        this.render();
      }

      disconnectedCallback() {
        // The sticky bar lives on <body>; take it away with the section (theme editor reloads)
        if (this.onStickyScroll) {
          window.removeEventListener('scroll', this.onStickyScroll);
          window.removeEventListener('resize', this.onStickyScroll);
        }
        if (this.stickyMq && this.onStickyMq) this.stickyMq.removeEventListener('change', this.onStickyMq);
        if (this.sticky && this.sticky.parentNode) this.sticky.remove();
        document.body.classList.remove('so-sticky-on');
      }

      onChange(event) {
        const input = event.target;
        // The notify form and the gift fields handle their own changes
        if (input.closest('[data-bis]') || input.closest('[data-gift]')) return;
        if (input === this.ackInput) {
          if (input.checked) this.ackError(false);
        } else if (input.closest('.so-bb__option')) {
          this.selectVariantFromOptions();
        } else if (input.name && input.name.indexOf('so-purchase-') === 0) {
          this.planId = input.value || '';
          if (this.planId) {
            const row = input.closest('.so-bb__row--sub');
            const chip = row && row.querySelector('.so-bb__freq input:checked');
            if (chip) this.planId = chip.value;
          }
        } else if (input.name && input.name.indexOf('so-freq-') === 0) {
          const row = input.closest('.so-bb__row--sub');
          const radio = row && row.querySelector('.so-bb__radio');
          if (radio) {
            radio.value = input.value;
            radio.checked = true;
          }
          this.planId = input.value;
        }
        this.render();
      }

      selectVariantFromOptions() {
        const selected = Array.from(this.querySelectorAll('.so-bb__option')).map((fs) => {
          const checked = fs.querySelector('input:checked');
          return checked ? checked.value : null;
        });
        const match = this.product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
        if (match) {
          this.variant = match;
          if (match.media) this.showMedia(match.media);
          const url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      priceFor(variant, planId) {
        if (planId && variant.plans && variant.plans[planId] !== undefined) return variant.plans[planId];
        return variant.price;
      }

      addonPrice() {
        if (!this.addon) return 0;
        if (this.planId && this.addon.plans && this.addon.plans[this.planId] !== undefined) return this.addon.plans[this.planId];
        return this.addon.price;
      }

      money(cents) {
        const whole = cents % 100 === 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      }

      // Prices change in place: the new value shows at once and fades up briefly (so-motion.css .so-tick).
      setText(el, text) {
        if (!el || el.textContent === text) return;
        el.textContent = text;
        el.classList.remove('so-tick');
        void el.offsetWidth;
        el.classList.add('so-tick');
      }

      render() {
        const v = this.variant;
        this.variantInput.value = v.id;
        this.planInput.value = this.planId;

        // Tiles
        this.querySelectorAll('.so-bb__option').forEach((fs, i) => {
          fs.querySelectorAll('.so-bb__tile').forEach((tile) => {
            const input = tile.querySelector('input');
            const on = input.value === v.options[i];
            input.checked = on;
            tile.classList.toggle('is-selected', on);
          });
        });

        // Purchase rows
        const onetimePrice = this.querySelector('[data-price-onetime]');
        this.setText(onetimePrice, this.money(v.price));
        this.querySelectorAll('.so-bb__purchase .so-bb__row').forEach((row) => {
          const radio = row.querySelector('.so-bb__radio');
          const on = radio ? radio.checked : false;
          row.classList.toggle('is-selected', on);
          if (row.dataset.row === 'subscribe') {
            const chip = row.querySelector('.so-bb__freq input:checked');
            const rowPlan = chip ? chip.value : radio.value;
            const priceEl = row.querySelector('[data-price-plan]');
            this.setText(priceEl, this.money(this.priceFor(v, rowPlan)));
            row.querySelectorAll('.so-bb__chip').forEach((c) =>
              c.classList.toggle('is-selected', c.querySelector('input').checked)
            );
          }
        });

        // Add-on
        const addonOn = !!(this.addonInput && this.addonInput.checked);
        const addonRow = this.querySelector('[data-addon]');
        if (addonRow) {
          addonRow.classList.toggle('is-selected', addonOn);
          const ap = addonRow.querySelector('[data-addon-price]');
          this.setText(ap, '+' + this.money(this.addonPrice()));
        }

        // Button
        const total = this.priceFor(v, this.planId) + (addonOn ? this.addonPrice() : 0);
        this.total = total;
        if (v.available) {
          this.atc.disabled = false;
          // Stays clickable so a click can explain what is missing; looks inactive until the box is ticked
          if (this.ackInput && !this.ackInput.checked) this.atc.setAttribute('aria-disabled', 'true');
          else this.atc.removeAttribute('aria-disabled');
          this.setText(this.atcLabel, `${this.buttonLabel} · ${this.money(total)}`);
        } else {
          this.atc.disabled = true;
          this.setText(this.atcLabel, this.soldOutLabel);
        }
        // Back in stock on: a sold out variant shows the notify form in place of the button
        this.atc.hidden = !v.available && !!this.bis;
        if (this.bis) this.renderBackInStock();

        // Express checkout: only for an available variant (the buttons read the id and selling_plan inputs above)
        if (this.express) this.express.hidden = !v.available;

        if (this.holiday) this.holiday.hidden = !v.available || this.holidayExpired === true;

        // Restocking soon label above the title: only while the selected size is sold out
        if (this.restock) this.restock.hidden = v.available;

        this.renderSticky();
      }

      showMedia(mediaId) {
        if (!mediaId) return;
        const id = String(mediaId);
        this.querySelectorAll('.so-bb__slide').forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === id));
        this.querySelectorAll('.so-bb__thumb').forEach((t) => {
          const on = t.dataset.mediaId === id;
          t.classList.toggle('is-active', on);
          if (on) t.setAttribute('aria-current', 'true');
          else t.removeAttribute('aria-current');
        });
      }

      // Line item properties from the form (gift card recipient fields), as Dawn's product-form.js sends them.
      // Fields the recipient form has disabled are left out by FormData; blank optional fields are skipped.
      formProperties() {
        const props = {};
        if (!this.form) return props;
        new FormData(this.form).forEach((value, key) => {
          const m = key.match(/^properties\[(.+)\]$/);
          if (m && typeof value === 'string' && value.trim() !== '') props[m[1]] = value;
        });
        return props;
      }

      giftOn() {
        return !!(this.gift && this.gift.checkboxInput && this.gift.checkboxInput.checked);
      }

      // A gift sent by email needs a recipient address; catch a missing or mistyped one before the cart does
      giftValid() {
        if (!this.giftOn()) return true;
        const g = this.gift;
        const email = (g.emailInput && g.emailInput.value.trim()) || '';
        if (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          if (typeof g.clearErrorMessage === 'function') g.clearErrorMessage();
          return true;
        }
        if (typeof g.displayErrorMessage === 'function') {
          g.displayErrorMessage('', { email: [email ? 'Please check the recipient email address' : 'Please enter the recipient email address'] });
        }
        if (g.emailInput) g.emailInput.focus();
        return false;
      }

      buildCartItems() {
        const items = [];
        const main = { id: this.variant.id, quantity: 1 };
        if (this.planId) main.selling_plan = Number(this.planId);
        const props = this.formProperties();
        if (this.ackInput && this.ackLabel) {
          // Shown on the line and order; the wording version is private (leading underscore) but kept on the order
          props[this.ackLabel] = this.ackValue;
          if (this.ackVersion) props[`_${this.ackLabel} version`] = this.ackVersion;
        }
        if (Object.keys(props).length) main.properties = props;
        items.push(main);
        if (this.addon && this.addonInput && this.addonInput.checked) {
          const add = { id: this.addon.id, quantity: 1 };
          if (this.planId && this.addon.plans && this.addon.plans[this.planId] !== undefined) add.selling_plan = Number(this.planId);
          items.push(add);
        }
        return items;
      }

      async onSubmit(event) {
        event.preventDefault();
        const fromSticky = this.stickyAdding === true;
        this.stickyAdding = false;
        if (this.atc.disabled) return;
        if (this.ackInput && !this.ackInput.checked) {
          this.ackError(true);
          this.ackInput.focus();
          return;
        }
        if (!this.giftValid()) return;
        this.errorEl.hidden = true;
        this.atc.setAttribute('aria-busy', 'true');
        if (this.stickyBtn && fromSticky) this.stickyBtn.setAttribute('aria-busy', 'true');

        const items = this.buildCartItems();
        const giftSent = this.giftOn();

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(fromSticky && this.stickyBtn ? this.stickyBtn : this.atc);
        }

        try {
          const res = await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!res.ok || data.status) {
            this.showError(data.description || data.message || 'Something went wrong. Please try again.');
            if (giftSent && data.errors && typeof data.errors === 'object' && typeof this.gift.displayErrorMessage === 'function') {
              this.gift.displayErrorMessage(data.message, data.errors);
            }
            if (fromSticky) this.scrollToBuy(this.errorEl);
            return;
          }
          // Confirmation on the button for two seconds (styles in so-motion.css).
          this.atc.classList.add('is-added');
          clearTimeout(this.addedTimer);
          this.addedTimer = setTimeout(() => this.atc.classList.remove('is-added'), 2000);
          // Gift sent: clear the recipient fields for the next one, as Dawn does after an add
          if (giftSent && typeof this.gift.resetRecipientForm === 'function') {
            this.gift.resetRecipientForm();
            if (typeof this.gift.onChange === 'function') this.gift.onChange();
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'so-buy-box',
              productVariantId: this.variant.id,
              variantIds: items.map((i) => i.id),
              cartData: data,
            });
          }
          if (cart && typeof cart.renderContents === 'function' && data.sections) {
            const first = data.items && data.items[0];
            cart.renderContents({ id: first ? first.id : this.variant.id, sections: data.sections });
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (e) {
          this.showError('Network error. Please try again.');
          if (fromSticky) this.scrollToBuy(this.errorEl);
        } finally {
          this.atc.removeAttribute('aria-busy');
          if (this.stickyBtn) this.stickyBtn.removeAttribute('aria-busy');
        }
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      ackError(on) {
        if (this.ackMsg) this.ackMsg.hidden = !on;
        const box = this.ackInput && this.ackInput.closest('[data-ack]');
        if (box) box.classList.toggle('is-invalid', on);
      }

      reducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      // Brings a part of the buy box into view (from the sticky bar), then moves focus there without a second jump
      scrollToBuy(target, focusEl) {
        const el = target || this.atc;
        if (!el) return;
        el.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'center' });
        if (focusEl) focusEl.focus({ preventScroll: true });
      }

      /* ---------- Read more (full product description when no short description is set) ---------- */

      // Without this script the whole description shows, so nothing is ever hidden for good.
      setupReadMore() {
        const box = this.querySelector('[data-readmore]');
        const btn = this.querySelector('[data-readmore-btn]');
        if (!box || !btn) return;
        // Pasted descriptions carry empty spacer paragraphs (<p>&nbsp;</p>); the type spacing already covers them
        Array.from(box.children).forEach((el) => {
          if (/^(P|DIV|H3|H4)$/.test(el.tagName) && !el.textContent.replace(/ /g, ' ').trim() && !el.querySelector('img, hr, iframe, video')) el.remove();
        });
        const limit = parseFloat(getComputedStyle(box).getPropertyValue('--so-desc-clamp')) || 196;
        // Leave it open when clamping would hide less than about two lines
        if (box.scrollHeight <= limit + 56) return;
        const moreLabel = btn.textContent.trim();
        const lessLabel = btn.dataset.less || 'Read less';
        box.classList.add('is-collapsible', 'is-clamped');
        btn.hidden = false;
        btn.addEventListener('click', () => {
          clearTimeout(this.readMoreTimer);
          if (btn.getAttribute('aria-expanded') === 'true') {
            // Close from the open height so the transition has a start point
            box.style.maxHeight = `${box.scrollHeight}px`;
            void box.offsetHeight;
            box.classList.add('is-clamped');
            box.style.maxHeight = '';
            btn.setAttribute('aria-expanded', 'false');
            btn.textContent = moreLabel;
            // A long description closed from far down the page: bring its start back into view
            if (box.getBoundingClientRect().top < 0) box.scrollIntoView({ block: 'start' });
          } else {
            box.style.maxHeight = `${box.scrollHeight}px`;
            box.classList.remove('is-clamped');
            btn.setAttribute('aria-expanded', 'true');
            btn.textContent = lessLabel;
            // After the transition, drop the fixed height so a resize never cuts the text
            this.readMoreTimer = setTimeout(() => { box.style.maxHeight = 'none'; }, 500);
          }
        });
      }

      /* ---------- Strength request tracker (by-review products, 2026-10-06) ---------- */

      // The contact form still emails the store. On submit the answers wait in sessionStorage; once Shopify
      // accepts the form (the page reloads with the sent message, which carries the webhook address), they go
      // to the tracker webhook, which logs the request in the Strength Requests sheet and drafts the order.
      setupRequestTracker() {
        const KEY = 'so-rq-pending';
        const form = this.querySelector('form.so-rq__form');
        if (form && form.querySelector('[name="contact[email]"]')) {
          form.addEventListener('submit', () => {
            const val = (key) => {
              const el = form.querySelector(`[name="contact[${key}]"]`);
              return el ? el.value.trim() : '';
            };
            const ack = form.querySelector('.so-ack__input');
            try {
              sessionStorage.setItem(KEY, JSON.stringify({
                at: Date.now(),
                path: window.location.pathname,
                name: val('name'),
                email: val('email'),
                current_strength: val('Current strength and how long'),
                skin_response: val('How skin has responded'),
                reactions: val('Reactions'),
                recent_order: val('Recent order number'),
                acknowledged: ack ? (ack.checked ? ack.value : 'Not checked') : 'Not shown',
              }));
            } catch (e) {}
          });
        }

        const done = this.querySelector('[data-rq-hook]');
        if (!done) return;
        let saved = null;
        try {
          saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
          // Clear first, so a refresh of the sent page never logs the request twice
          sessionStorage.removeItem(KEY);
        } catch (e) {}
        if (!saved || !saved.email || saved.path !== window.location.pathname) return;
        if (Date.now() - saved.at > 30 * 60 * 1000) return;

        const d = done.dataset;
        let stamp;
        try {
          // Store time (Charleston), as 2026-10-06 14:05 so the sheet reads it as a date
          stamp = new Date().toLocaleString('sv-SE', { timeZone: 'America/New_York' }).slice(0, 16);
        } catch (e) {
          stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
        }
        // Integrately needs a last name to create the Shopify customer: a one-word name goes there
        const parts = saved.name.split(/\s+/).filter(Boolean);
        const firstName = parts.length > 1 ? parts[0] : '';
        const lastName = parts.length > 1 ? parts.slice(1).join(' ') : parts[0] || '';
        const level = d.rqStrength.replace(/^Strength\s+/i, '').toLowerCase();
        const note = [
          `Strength review request: ${d.rqStrength}`,
          `Current strength and how long: ${saved.current_strength}`,
          `How skin has responded: ${saved.skin_response}`,
          `Reactions: ${saved.reactions}`,
          `Recent order: ${saved.recent_order || 'Not given'}`,
          `Acknowledgment: ${saved.acknowledged}`,
          `Submitted: ${stamp}`,
        ].join('\n');
        const body = new URLSearchParams({
          submitted_at: stamp,
          strength: d.rqStrength,
          product_title: d.rqProductTitle,
          product_id: d.rqProductId,
          variant_id: d.rqVariantId,
          variant_gid: `gid://shopify/ProductVariant/${d.rqVariantId}`,
          price: d.rqPrice || '',
          quantity: '1',
          name: saved.name,
          first_name: firstName,
          last_name: lastName,
          email: saved.email,
          current_strength: saved.current_strength,
          skin_response: saved.skin_response,
          reactions: saved.reactions,
          recent_order: saved.recent_order,
          acknowledged: saved.acknowledged,
          page_url: d.rqPage,
          tags: `strength-review, strength-${level}`,
          note,
        });
        // Form-encoded and no-cors: a simple request, so no preflight. The email copy is the backup if this fails.
        fetch(d.rqHook, { method: 'POST', mode: 'no-cors', keepalive: true, body }).catch(() => {});
      }

      /* ---------- Back in stock (Klaviyo client API: public key only, no private key) ---------- */

      setupBackInStock() {
        this.bis = this.querySelector('[data-bis]');
        if (!this.bis) return;
        this.bisCompany = this.dataset.bisCompany || '';
        this.bisForm = this.bis.querySelector('[data-bis-form]');
        this.bisEmail = this.bis.querySelector('[data-bis-email]');
        this.bisSubmitBtn = this.bis.querySelector('[data-bis-submit]');
        this.bisMsg = this.bis.querySelector('[data-bis-msg]');
        this.bisVariantEl = this.bis.querySelector('[data-bis-variant]');
        this.bisForm.addEventListener('submit', this.onBisSubmit.bind(this));
        this.bisEmail.addEventListener('input', () => {
          if (this.bisEmail.getAttribute('aria-invalid') === 'true') {
            this.bisEmail.removeAttribute('aria-invalid');
            this.bisMessage('');
          }
        });
      }

      renderBackInStock() {
        const v = this.variant;
        this.bis.hidden = v.available;
        if (this.bisVariantEl) this.bisVariantEl.textContent = this.product.defaultVariant ? '' : ` · ${v.title}`;
        // A different sold out size starts a fresh signup
        if (this.bisDoneFor && this.bisDoneFor !== v.id) {
          this.bisDoneFor = null;
          this.bisForm.classList.remove('is-done');
          this.bisMessage('');
        }
      }

      bisMessage(kind) {
        if (!this.bisMsg) return;
        const text = kind ? this.bisMsg.dataset[kind] || '' : '';
        this.bisMsg.textContent = text;
        if (kind) this.bisMsg.dataset.state = kind;
        else delete this.bisMsg.dataset.state;
      }

      // The request on its own, so it can be checked without sending (fetch stubbed in testing)
      bisRequest(email, variantId) {
        return {
          url: `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.bisCompany)}`,
          init: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  channels: ['EMAIL'],
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
                relationships: {
                  variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
                },
              },
            }),
          },
        };
      }

      async onBisSubmit(event) {
        event.preventDefault();
        if (this.bisBusy) return;
        const email = this.bisEmail.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          this.bisEmail.setAttribute('aria-invalid', 'true');
          this.bisMessage('invalid');
          this.bisEmail.focus();
          return;
        }
        this.bisEmail.removeAttribute('aria-invalid');
        const variantId = this.variant.id;
        const req = this.bisRequest(email, variantId);
        this.bisBusy = true;
        this.bisSubmitBtn.setAttribute('aria-busy', 'true');
        this.bisMessage('');
        try {
          const res = await fetch(req.url, req.init);
          if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
          this.bisDoneFor = variantId;
          this.bisForm.classList.add('is-done');
          this.bisMessage('success');
        } catch (e) {
          this.bisMessage('error');
        } finally {
          this.bisBusy = false;
          this.bisSubmitBtn.removeAttribute('aria-busy');
        }
      }

      /* ---------- Holiday order-by note ---------- */

      // Liquid hides the note after its end date; this repeats the check for pages served from cache
      checkHoliday() {
        const el = this.holiday;
        if (!el || !el.dataset.holidayEnd) return;
        const tz = /^[+-]\d{4}$/.test(el.dataset.holidayTz || '') ? el.dataset.holidayTz : '+0000';
        const end = Date.parse(`${el.dataset.holidayEnd}T23:59:59${tz.slice(0, 3)}:${tz.slice(3)}`);
        if (!Number.isNaN(end) && Date.now() > end) {
          this.holidayExpired = true;
          el.hidden = true;
        }
      }

      /* ---------- Sticky Add to Bag (phones) ---------- */

      setupSticky() {
        this.sticky = this.querySelector('[data-sticky]');
        if (!this.sticky || !this.atc) return;
        this.stickyBtn = this.sticky.querySelector('[data-sticky-btn]');
        this.stickyPrice = this.sticky.querySelector('[data-sticky-price]');
        this.stickyVariant = this.sticky.querySelector('[data-sticky-variant]');
        // Outside the section so no transformed or clipped parent can trap a fixed bar
        document.body.appendChild(this.sticky);
        this.sticky.hidden = false;
        this.sticky.setAttribute('inert', '');
        this.stickyBtn.addEventListener('click', this.onStickyClick.bind(this));
        this.stickyMq = window.matchMedia('(max-width: 749px)');
        this.onStickyMq = () => this.updateSticky();
        this.stickyMq.addEventListener('change', this.onStickyMq);
        // Watches the main button (or the notify form while sold out); the bar shows once it has scrolled up past
        // the top. A scroll check (once per frame) also catches jumps an IntersectionObserver would miss, such as a
        // restored scroll position or an anchor link straight past the button.
        this.onStickyScroll = () => {
          if (this.stickyFrame) return;
          this.stickyFrame = requestAnimationFrame(() => {
            this.stickyFrame = null;
            this.updateSticky();
          });
        };
        window.addEventListener('scroll', this.onStickyScroll, { passive: true });
        window.addEventListener('resize', this.onStickyScroll, { passive: true });
      }

      stickyAnchor() {
        return !this.variant.available && this.bis ? this.bis : this.atc;
      }

      updateSticky() {
        if (!this.sticky) return;
        const anchor = this.stickyAnchor();
        const rect = anchor.getBoundingClientRect();
        const passed = rect.height > 0 && rect.bottom < 0;
        const on = passed && this.stickyMq.matches;
        if (on === this.stickyOn) return;
        this.stickyOn = on;
        this.sticky.classList.toggle('is-visible', on);
        if (on) this.sticky.removeAttribute('inert');
        else this.sticky.setAttribute('inert', '');
        // Room at the end of the page so the bar never covers the footer's last line
        document.body.classList.toggle('so-sticky-on', on);
      }

      renderSticky() {
        if (!this.sticky || !this.stickyBtn) return;
        const v = this.variant;
        this.setText(this.stickyPrice, v.available ? this.money(this.total) : this.soldOutLabel);
        if (this.stickyVariant) this.stickyVariant.textContent = v.title;
        let label = this.buttonLabel;
        if (!v.available) label = this.bis ? this.stickyBtn.dataset.notifyLabel || this.soldOutLabel : this.soldOutLabel;
        this.stickyBtn.textContent = label;
        this.updateSticky();
      }

      onStickyClick() {
        const v = this.variant;
        if (!v.available) {
          // Sold out: take the shopper to the notify form (or the size choice when there is none)
          if (this.bis) this.scrollToBuy(this.bis, this.bisEmail);
          else this.scrollToBuy(this.querySelector('.so-bb__option') || this.atc);
          return;
        }
        if (this.ackInput && !this.ackInput.checked) {
          // Never adds past the acknowledgment: show the warning and the checkbox instead
          this.ackError(true);
          this.scrollToBuy(this.ackInput.closest('.so-bb__notice') || this.ackInput, this.ackInput);
          return;
        }
        if (this.giftOn() && !this.giftValid()) {
          this.scrollToBuy(this.gift);
          return;
        }
        // Same path as the main button
        this.stickyAdding = true;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
        else this.atc.click();
      }
    }
  );
}
